import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addAuftragPosition,
    AuftragPosition,
    defaultAuftragPosition,
    updateAuftragPosition,
    useAuftragPositionList,
} from 'api/auftragPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { Kunde } from 'api/kunde';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { Auftrag } from 'api/auftrag';
import { useArtikelDetail } from 'api/artikelDetail';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { AuftragPositionChangeMenge } from './AuftragPositionChangeMenge';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    addLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '6fr 1fr',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface AuftragPositionEditProps {
    kunde: Kunde;
    auftrag: Auftrag;
    auNr: string;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const AuftragPositionEdit: React.FC<AuftragPositionEditProps> = ({
    kunde,
    auftrag,
    auNr,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [validationWarning, setValidationWarning] = React.useState<string | null>(null);
    const isMounted = useIsMounted();
    const [showChangeMenge, setShowChangeMenge] = React.useState(false);

    const auftragPositionList = useAuftragPositionList(
        {
            auNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [auftragPosition, setAuftragPosition] = React.useState<AuftragPosition | null>(null);

    React.useEffect(() => {
        if (!auftragPositionList.isLoading && !auftragPositionList.isFetching && auftragPosition === null) {
            if (auftragPositionList.data?.length === 1) {
                setAuftragPosition(cloneDeep(auftragPositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (auftragPositionList.data[0].artikelGruppeNr && auftragPositionList.data[0].artikelNr) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: auftragPositionList.data[0].artikelGruppeNr,
                        artikelNr: auftragPositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setAuftragPosition({
                    ...defaultAuftragPosition,
                    kNr: kunde.nr,
                    auNr,
                    pos,
                    posMwst: kunde.land === 'AT' ? 20 : 0,
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [auftragPosition, auftragPositionList, auNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !auftragPositionList.isLoading &&
            !auftragPositionList.isFetching &&
            auftragPositionList.data?.length === 1 &&
            auftragPosition !== null &&
            !isEqual(auftragPositionList.data[0], auftragPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('auftragPosition', auftragPosition, auftragPositionList.data[0]);
        }
    }, [savingState, auftragPositionList, auftragPosition]);

    React.useEffect(() => {
        if (auftragPosition) {
            if (auftragPosition.posMenge && auftragPosition.posEPreis) {
                const netto = Math.round(auftragPosition.posMenge * auftragPosition.posEPreis * 100) / 100;
                const mwstFactor = auftragPosition.posMwst ? 1 + auftragPosition?.posMwst / 100 : 1;
                const brutto = auftragPosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;

                setAuftragPosition({
                    ...auftragPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setAuftragPosition({
                    ...auftragPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auftragPosition?.posMenge, auftragPosition?.posEPreis, auftragPosition?.posMwst]);

    const artikelDetail = useArtikelDetail(
        {
            artikelGruppeNr: auftragPosition?.artikelGruppeNr,
            artikelNr: auftragPosition?.artikelNr,
        },
        {
            enabled: !!auftragPosition?.artikelGruppeNr && !!auftragPosition?.artikelNr,
        },
    );

    React.useEffect(() => {
        if (
            auftragPosition &&
            ['L', 'R'].includes(auftragPosition.lArt) &&
            artikelDetail.data &&
            artikelDetail.data.bestand - artikelDetail.data.offenRes - auftragPosition.posMenge <
                artikelDetail.data.minBestand
        ) {
            setValidationWarning('Mindestbestand unterschritten!');
        } else if (validationWarning) {
            setValidationWarning(null);
        }
    }, [artikelDetail.data, auftragPosition, validationWarning]);

    const validate = (): boolean => {
        if (!auftragPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (auftragPosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (auftragPosition.pos === -1) {
                    addAuftragPosition(auftragPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateAuftragPosition(auftragPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (column: keyof AuftragPosition, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(auftragPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (auftragPosition) {
                    setAuftragPosition({
                        ...auftragPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof AuftragPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(auftragPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (auftragPosition) {
                    setAuftragPosition({
                        ...auftragPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof AuftragPosition, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={auftragPosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (auftragPosition) {
                    setAuftragPosition({
                        ...auftragPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={auftragPosition?.artikelGruppeNr}
            artikelNr={auftragPosition?.artikelNr}
            disabled={auftragPosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (auftragPosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        setAuftragPosition({
                            ...auftragPosition,
                            artikelGruppeNr: defaultAuftragPosition.artikelGruppeNr,
                            artikelNr: defaultAuftragPosition.artikelNr,
                            posText: defaultAuftragPosition.posText,
                            posEkPreis: defaultAuftragPosition.posEkPreis,
                            posEPreis: defaultAuftragPosition.posEPreis,
                            posEinh: defaultAuftragPosition.posEinh,
                            lArt: defaultAuftragPosition.lArt,
                        });
                        return;
                    }

                    setArtikelDetailKalkulationen(null);

                    const newArtikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                    });

                    setArtikelDetailKalkulationen(newArtikelDetailKalkulationen);

                    const defaultKalkulation = newArtikelDetailKalkulationen.find(
                        (k) => k.eh === newArtikelDetail.artikel?.einheit,
                    );

                    const ekPreis = defaultKalkulation?.ek2 || 0;
                    const ePreis = defaultKalkulation?.vk || 0;

                    setAuftragPosition({
                        ...auftragPosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel?.text || '',
                        posEkPreis: ekPreis,
                        posEPreis: ePreis,
                        posEinh: newArtikelDetail.artikel?.einheit || '',
                        lArt: newArtikelDetail.lagerIntNr === 0 ? '' : 'R',
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof AuftragPosition,
        ekPreisColumn: keyof AuftragPosition,
        ePreisColumn: keyof AuftragPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(auftragPosition?.[column] as string) || ''}
            menuPlacement="top"
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek2 || 0;
                    // ePreis = Math.round(ekPreis * ((100 + (kalkulation?.vkProz || 0)) / 100) * 1000) / 1000;
                    ePreis = kalkulation?.vk || 0;
                }

                if (auftragPosition) {
                    setAuftragPosition({
                        ...auftragPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    const renderLieferArt = () => (
        <LieferArt
            allowEmpty
            disabled={savingState === SavingState.saving || readOnly || pos !== -1}
            value={(auftragPosition?.lArt as string) || ''}
            menuPlacement="top"
            onChange={(newValue) => {
                if (auftragPosition) {
                    setAuftragPosition({
                        ...auftragPosition,
                        lArt: newValue || '',
                    });
                }
            }}
        />
    );

    return (
        <>
            <Dialog
                hidden={false}
                title="Auftragsposition"
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={1100}
                onDismiss={() => {
                    onClose(false);
                }}
            >
                <form className={formStyles.form}>
                    <div className={styles.artikelLine}>
                        <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                        <div>{renderArtikelDetailSelect()}</div>
                    </div>
                    <div className={formStyles.line}>
                        <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                    </div>
                    <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                    <div className={styles.addLine}>
                        <div />
                        <div>{renderLieferArt()}</div>
                    </div>
                    <div className={styles.posLine}>
                        <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                        <div>
                            {renderDecimalField('posMenge', 'Menge', {
                                fixed: 3,
                                disabled: pos !== -1,
                                onChange: (newValue) => {
                                    if (auftragPosition) {
                                        setAuftragPosition({
                                            ...auftragPosition,
                                            posMenge: newValue,
                                            offen: newValue,
                                        });
                                    }
                                },
                            })}
                        </div>
                        <div>{renderDecimalField('offen', 'Offen', { fixed: 3, disabled: true })}</div>
                        <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posEPreis', 'VK-Preis', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posNetto', 'VK Netto')}</div>
                        <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                        <div>{renderDecimalField('posBrutto', 'VK Brutto')}</div>
                    </div>

                    <Timestamps
                        modifiedDate={auftragPosition?.updDat}
                        modifiedDecimalTime={auftragPosition?.updUhr}
                        modifiedUser={auftragPosition?.updUser}
                        showCreated={false}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {validationWarning && <div className={formStyles.warn}>{validationWarning}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => setShowChangeMenge(true)}
                            text="Lieferart / Menge ändern"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                auftragPositionList.isLoading ||
                                !auftragPosition?.pos
                            }
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || auftragPositionList.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(auftragPositionList.isLoading || auftragPositionList.isFetching || !auftragPosition) && (
                    <LoadingIndicator />
                )}
            </Dialog>
            {showChangeMenge && auftragPosition && (
                <AuftragPositionChangeMenge
                    auftrag={auftrag}
                    auftragPosition={auftragPosition}
                    onClose={(refetch) => {
                        setShowChangeMenge(false);
                        if (refetch) {
                            onClose(true);
                        }
                    }}
                />
            )}
        </>
    );
};
